import React, { useState, useEffect, useCallback } from "react";
import _get from "lodash/get";

import PromoBanner from "@containers/Promo/PromoBanner";
import PromoMainMenu from "@containers/Promo/PromoMainMenu";
import PromoMobileMenu from "@containers/Promo/PromoMobileMenu";
import PromoMobileFilters from "@containers/Promo/PromoMobileProductsFilters";
import PromoMobileProductsFiltersToggle from "@containers/Promo/PromoMobileProductsFiltersToggle";
import ProductsCategoryAllProducts from "@containers/Promo/PromoCategoryAllProducts";
import PromoCategory from "@containers/Promo/PromoCategory";
import PromoProducts from "@containers/Promo/PromoProducts";
import Heading from "@components/Heading";
import CompareWidget from "@containers/Catalog/CompareWidget";
import PromoFooter from "@containers/Promo/PromoFooter";
import PromoScrollTop from "@containers/Promo/PromoScrollTop";
import PromoProductsSort from "@containers/Promo/PromoProductsSort";
import PromoMobileProductsSort from "@containers/Promo/PromoMobileProductsSort";
import PromoBreadcrumbs from "@containers/Promo/PromoBreadcrumbs";
import PromoHomepageCategories from "./PromoHomepageCategories";
import getConfig from "next/config";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const isBricoStore = websiteName === "bricostore";

const Promo = ({ campaign, tree, category, products, page, context }) => {
  const [sortType, setSortType] = useState("default");
  const [filters, setFilters] = useState({});
  const [isMobileFiltersVisible, setMobileFiltersVisibility] = useState(false);

  const hasProducts =
    page.layout === "products" || page.layout === "all-products";
  const hasMenu =
    (tree && tree.length > 1) ||
    (tree && tree.length === 1 && tree[0].subcategories?.length > 0);
  const hasFooterLinks =
    _get(campaign, "properties.footer_links", []).length !== 0;
  const hasFilters = category.filters;
  const hasBanner = _get(campaign, "properties.banner1", "");
  const hasDarkTheme = page.theme === "dark";

  useEffect(() => {
    setSortType("default");
    setFilters({});
  }, [category.id, page.layout]);

  const handleSortTypeChange = (type) => setSortType(type);
  const handleMobileFiltersVisibility = () =>
    setMobileFiltersVisibility(!isMobileFiltersVisible);

  const handleFiltersChange = useCallback(
    ({ filter, option }) => {
      const newFilters = { ...filters };

      // Filter is already set
      if (newFilters[filter.attribute_code]) {
        const filterValueIndex = newFilters[filter.attribute_code].indexOf(
          option.value
        );

        // Update existing filter
        if (filterValueIndex > -1) {
          newFilters[filter.attribute_code].splice(filterValueIndex, 1);

          // Delete filter if it has no options set
          if (!newFilters[filter.attribute_code].length) {
            delete newFilters[filter.attribute_code];
          }
        } else {
          // Add filter option
          newFilters[filter.attribute_code].push(option.value);
        }
      } else {
        // Create filter
        newFilters[filter.attribute_code] = [option.value];
      }

      setFilters(newFilters);
    },
    [filters]
  );

  const isTeaser = campaign.is_teaser === 1;

  return (
    <div
      className={
        hasDarkTheme ? "group group-promoDarkTheme bg-[#111111]" : "bg-gray-100"
      }
    >
      {hasBanner && (
        <div className="flex justify-center">
          <PromoBanner
            large={campaign.properties.banner1}
            medium={campaign.properties.banner2}
            small={campaign.properties.banner3}
          />
        </div>
      )}

      {page.layout !== "homepage-categories" ? (
        <>
          {hasMenu && (
            <div className="block lg:hidden">
              <PromoMobileMenu
                currentPageCategory={category.id}
                categories={tree}
                context={context}
                campaignSlug={campaign.slug}
              />
            </div>
          )}

          <div className="max-w-7xl mx-auto px-3 md:px-4">
            {hasMenu && (
              <div
                className={`pt-2 lg:pt-5 ${
                  isBricoStore ? "mb-3 lg:mb-4" : "mb-8 lg:mb-12"
                }`}
              >
                <PromoBreadcrumbs
                  campaignSlug={campaign.slug}
                  breadcrumbs={page.breadcrumbs}
                />
              </div>
            )}

            <div className={`lg:flex ${isBricoStore ? "mt-2 lg:mt-3" : "mt-10"}`}>
              {hasMenu && (
                <div className="hidden lg:block w-full max-w-[232px]" />
              )}

              <div
                className={`${hasMenu ? "lg:pl-8 " : ""}w-full${
                  isBricoStore
                    ? " lg:flex lg:items-center lg:justify-between lg:gap-4"
                    : ""
                }`}
              >
                {page.heading && (
                  <div
                    className={`${
                      isBricoStore ? "mb-2 lg:mb-0" : "mb-5"
                    } group-with-promoDarkTheme:text-white`}
                  >
                    <Heading display="h1" as="h1" title={page.heading} />
                  </div>
                )}

                {hasProducts && !isTeaser && (
                  <div className="hidden lg:flex lg:justify-end">
                    <PromoProductsSort
                      value={sortType}
                      onChange={handleSortTypeChange}
                    />
                  </div>
                )}
              </div>
            </div>

            {(hasProducts || hasFilters) && (
              <div className="flex-col-reverse flex sm:flex-row lg:hidden">
                <div className="w-full sm:w-1/2">
                  {hasFilters && (
                    <PromoMobileProductsFiltersToggle
                      onClick={handleMobileFiltersVisibility}
                      isActive={isMobileFiltersVisible}
                    />
                  )}
                </div>

                <div className="w-full sm:w-1/2 sm:pl-4 mb-3 sm:mb-0">
                  {hasProducts && !isTeaser && (
                    <PromoMobileProductsSort
                      value={sortType}
                      onChange={handleSortTypeChange}
                    />
                  )}
                </div>
              </div>
            )}

            {hasFilters && isMobileFiltersVisible && (
              <div className="block lg:hidden">
                <PromoMobileFilters
                  filters={category.filters}
                  activeFilters={filters}
                  onFiltersChange={handleFiltersChange}
                />
              </div>
            )}

            <div
              className={`lg:flex ${
                isBricoStore ? "mt-3 lg:mt-4" : "mt-10"
              } pb-32`}
            >
              {hasMenu && (
                <aside className="hidden lg:block w-full max-w-[232px]">
                  <PromoMainMenu
                    currentPageCategory={category.id}
                    categories={tree}
                    context={context}
                    campaignSlug={campaign.slug}
                    activeFilters={filters}
                    onFiltersChange={handleFiltersChange}
                  />
                </aside>
              )}

              <main className={hasMenu ? "lg:pl-8 w-full" : "w-full"}>
                {page.layout === "products" && (
                  <PromoProducts
                    category={category}
                    categories={tree}
                    products={products}
                    context={context}
                    campaign={campaign}
                    sort={sortType}
                    filters={filters}
                  />
                )}

                {page.layout === "all-products" && (
                  <ProductsCategoryAllProducts
                    page={page}
                    campaign={campaign}
                    context={context}
                    category={category}
                    products={products}
                    filters={filters}
                    sort={sortType}
                  />
                )}

                {page.layout === "category" && (
                  <PromoCategory
                    page={page}
                    campaign={campaign}
                    category={category}
                  />
                )}
              </main>
            </div>
          </div>
        </>
      ) : (
        <div className="max-w-7xl mx-auto px-3 md:px-4">
          <main className="w-full">
            <PromoHomepageCategories campaign={campaign} tree={tree} />
          </main>
        </div>
      )}

      {hasFooterLinks && (
        <PromoFooter links={campaign.properties.footer_links} />
      )}

      <PromoScrollTop />
      <CompareWidget />
    </div>
  );
};

export default Promo;
